import {endOfMonth, format, startOfMonth} from 'date-fns';
import {REPORT_TYPE} from '../../../constants';
import {ReportState} from '../../../forecast-app/reports-tab/reports-page/ReportTypes';
import {Condition, FieldNode, ReportDefinitionV1} from '../contracts/report-definition.types';
import {normalizeFieldTree} from './definitionFields';
import {effectiveReportPeriod} from '../periodFilter';
import {displayNameOf, grainOf, ReportGrain} from '../reportGrain';

/** The one grain the app authors itself rather than the agent authoring it. */
const AUDIT_LOG_GRAIN: ReportGrain = 'TIME_REG_AUDIT_LOG';

/** Saved-report filter row as the `saveReportAs` / `updateSavedReport` mutations accept it
 * (`GlobalSearchFilterType`). Same data as a report-definition condition, except the value list is
 * singular (`value`, not `values`). */
export interface SavedReportFilterInput {
	field: string;
	operator: Condition['operator'];
	value: string[];
	context: Condition['context'];
}

/** One visible data column on the wire, in display order. Width is omitted rather than null when the
 * definition does not set one, so the mutation input stays minimal. */
export interface SavedReportLayoutColumnInput {
	field: string;
	width?: number;
}

/** The column a saved report is sorted by. */
export interface SavedReportSortInput {
	field: string;
	direction: 'ASC' | 'DESC';
}

/** The four grouping slots a saved report carries, in place of the definition's grouping array. */
export interface SavedReportGroupingInput {
	groupingOne: string | null;
	groupingTwo: string | null;
	groupingThree: string | null;
	groupingFour: string | null;
}

/** What a save returns and a re-save needs back: the report's Relay id plus the fields an update
 * overwrites unconditionally and therefore has to echo (see `buildUpdateSavedReportInput`). */
/** A report that exists in storage.
 *
 * `id` is the RELAY GLOBAL ID — `base64("SavedReportType:<uuid>")` — as the save mutations return it,
 * not report-service's raw uuid. Everything downstream is built on that and none of it is a REST call
 * to report-service, however much the definition path looks like one:
 *
 * - the lifecycle mutations and `updateSavedReport` resolve their id with a bare `fromGlobalId`, which
 *   yields an empty id for anything else, so they fail silently on a raw uuid;
 * - `/report/definition/:reportId` is served by the graphql server, not report-service. It decodes via
 *   `toRawReportId` (`server.js`), which accepts either shape, so it is the one consumer that cannot
 *   tell you which id space is correct;
 * - `/report-builder/:reportId` carries whatever this holds, and the re-open path seeds this field
 *   straight back from that URL segment.
 *
 * Do not "correct" this to the raw uuid. Encoding at the mutation boundaries then double-encodes on the
 * re-open path, because the seed is already a global id — publish and discard then resolve to a report
 * that does not exist and fail with no error at all. */
export interface SavedReportRef {
	id: string;
	privateAccess: boolean;
}

/** `SaveReportAsInput` for creating a first-class saved report from a report definition. Declared
 * structurally rather than imported from the Relay artifact so this module and its tests compile
 * without a relay-compiler run. */
export interface SaveReportAsInputFields extends SavedReportGroupingInput {
	name: string;
	type: string;
	eyeApplied: string;
	privateAccess: boolean;
	filters: SavedReportFilterInput[];
	layout: SavedReportLayoutColumnInput[] | null;
	sort: SavedReportSortInput | null;
	startDate: string;
	endDate: string;
	datePreset: string | null;
	dateScope: string | null;
	/** Where the report was authored. 'AI' is the agent; 'STANDARD' covers a report the app built
	 * itself from a card, which the audit log is. It decides whether the surface shows the
	 * "Generated from your prompt" banner, so a report with no prompt behind it must not claim 'AI'. */
	source: 'AI' | 'STANDARD';
	promptText: string | null;
	manuallyEdited: boolean;
}

/** `UpdateSavedReportInput` for re-saving an already-saved report. */
export interface UpdateSavedReportInputFields extends SavedReportGroupingInput {
	id: string;
	name: string;
	eyeApplied: string;
	reportService: true;
	state: ReportState;
	privateAccess: boolean;
	filters: SavedReportFilterInput[];
	layout: SavedReportLayoutColumnInput[] | null;
	sort: SavedReportSortInput | null;
	startDate: string;
	endDate: string;
	datePreset: string | null;
	dateScope: string | null;
	manuallyEdited: boolean;
	lockToken?: string;
}

/** New reports are private to their creator; granting other people access is a separate share
 * action, not part of saving. */
const DEFAULT_PRIVATE_ACCESS = true;

/** Collects every field path in the tree as dot-notation, deepest-first: a node's children are
 * emitted before the node itself.
 *
 * The order matters. The saved-report wire format for columns is the eye-options object, and the
 * graphql server rebuilds the definition's field tree from that object's key order. Emitting a
 * parent before its children would make the rebuilt tree list the parent's own path first and
 * reorder the columns on re-open, so this mirrors the server's own traversal (see
 * `EyeOptionsMapper` / `FieldBuilder` in the graphql service) to keep column order stable across a
 * save/re-open round trip. */
function collectFieldPaths(fields: FieldNode[], prefix: string, paths: string[]): void {
	for (const field of fields) {
		const path = prefix ? `${prefix}.${field.name}` : field.name;
		if (field.children && field.children.length > 0) {
			collectFieldPaths(field.children, path, paths);
		}
		paths.push(path);
	}
}

/** Serialises the definition's field tree into the eye-options JSON string the saved-report
 * mutations take for columns (`{"task.name": true, "task": true, ...}`).
 *
 * Normalised first, because a definition does not always arrive as a tree: the drafting agent emits
 * some with every column as one flat dotted node. Walking that shape directly emits only the leaves,
 * so the tree the server rebuilds from these keys has no root and the report re-opens with its
 * columns gone - while rendering correctly in the session that saved it. Normalising makes both
 * shapes produce byte-identical eye options. */
export function toEyeApplied(fields: FieldNode[]): string {
	const paths: string[] = [];
	collectFieldPaths(normalizeFieldTree(fields), '', paths);
	const eyeOptions: Record<string, boolean> = {};
	for (const path of paths) {
		eyeOptions[path] = true;
	}
	return JSON.stringify(eyeOptions);
}

/** Maps the definition's filter conditions onto the saved-report filter rows. Conditions are
 * forwarded verbatim apart from `values` → `value`; nothing is dropped, so a re-opened report
 * filters exactly as the one that was saved. */
export function toSavedReportFilters(definition: ReportDefinitionV1): SavedReportFilterInput[] {
	return definition.filter.filterConditions.map(condition => ({
		field: condition.field,
		operator: condition.operator,
		value: condition.values,
		context: condition.context,
	}));
}

/** Spreads the definition's ordered grouping tokens across the four saved-report grouping slots.
 * Unused slots are explicitly null so a re-save clears a grouping level that was removed. */
export function toSavedReportGroupings(definition: ReportDefinitionV1): SavedReportGroupingInput {
	const grouping = (definition.grouping ?? []) as string[];
	return {
		groupingOne: grouping[0] ?? null,
		groupingTwo: grouping[1] ?? null,
		groupingThree: grouping[2] ?? null,
		groupingFour: grouping[3] ?? null,
	};
}

/** Maps the definition's column layout onto the saved-report layout rows: same field key and width,
 * with the list order carrying each column's left-to-right position. Null when the definition stores no
 * layout, which clears any layout the report had.
 *
 * Layout persists column ORDER and WIDTH only. It is not a visibility list: `serializeDefinition` omits
 * a column the session has hidden, but a re-opened report derives its column set from the field tree
 * (see `orderedDataColumnIds`), so a hidden column reappears. Hiding is session state; persisting it
 * needs a visibility concept in the definition contract, which does not exist. */
export function toSavedReportLayout(definition: ReportDefinitionV1): SavedReportLayoutColumnInput[] | null {
	if (!definition.layout) {
		return null;
	}
	return definition.layout.map(column =>
		column.width !== undefined ? {field: column.field, width: column.width} : {field: column.field}
	);
}

/** The definition's sort column, or null when the report applies no sort (which clears a stored one). */
export function toSavedReportSort(definition: ReportDefinitionV1): SavedReportSortInput | null {
	return definition.sort ? {field: definition.sort.field, direction: definition.sort.direction} : null;
}

/** The report's period, always populated.
 *
 * A saved report MUST carry both dates. Consumers of a saved report format them without null-guarding —
 * the Standard task report page turns a null date into the literal string `"Invalid date"` and sends it
 * to the project search, which parses it as a number and fails the whole page with a 500. Every report
 * created the Standard way has dates because the create action always seeds the current month, so an
 * AI-generated definition (which expresses its period as filter conditions and often carries no
 * top-level dates) has to seed the same default rather than save nulls.
 *
 * The window is recovered from the definition's own date conditions before falling back to the
 * current month, because seeding the current month over a definition that HAS a range persists a
 * date pair contradicting the conditions the report actually filters on. That lie is not inert: the
 * builder reads the stored pair back in preference to the conditions, so a Q4 deadline report
 * reopened in August reports an August window, and changing its scope rewrites the range to August
 * — silently moving the report's period. The current month is still the last resort, for a
 * definition that genuinely has no range at all. */
function datesOf(definition: ReportDefinitionV1): {startDate: string; endDate: string} {
	const today = new Date();
	// Through `effectiveReportPeriod`, the same derivation the date control renders from and the
	// fetch body reports against, so what a save PERSISTS cannot describe a different window from
	// the one the report was just showing and querying. Only the last-resort default is this
	// function's own.
	const period = effectiveReportPeriod(definition);
	return {
		startDate: period.startDate ?? format(startOfMonth(today), 'yyyy-MM-dd'),
		endDate: period.endDate ?? format(endOfMonth(today), 'yyyy-MM-dd'),
	};
}

/** Builds the create input for the first save of a report definition. `type` is the definition's
 * resolved grain (see `grainOf`), because the field is optional on the definition while the
 * mutation requires it. `promptText` is the request the report was generated from; pass null when
 * there is none, so a report saved without one records none. */
export function buildSaveReportAsInput(
	definition: ReportDefinitionV1,
	promptText: string | null,
	manuallyEdited: boolean
): SaveReportAsInputFields {
	// A resolved grain is written as its own display name. An UNRESOLVED grain means the definition
	// names a report this builder does not build (Utilization / Time Registered / Project Portfolio)
	// -- all three schema-valid and all three still live in report-service's ReportType. Relabelling
	// one of those to 'Task report' would silently change what the stored report IS, so the original
	// value is preserved and only a definition carrying no reportType at all falls back.
	const grain = grainOf(definition);
	const grainDisplayName = grain ? displayNameOf(grain) : definition.reportType ?? REPORT_TYPE.TASK_REPORT;
	return {
		name: definition.name,
		// Resolved, not read raw: the drafting agent strips `reportType` from a fallback
		// definition while leaving that grain's default columns in place, so a raw read would
		// record a project-status report as a task report. Uses the same rule as the taxonomy
		// fetch, so a report cannot be filtered as one grain and recorded as another. An
		// unresolved grain keeps the task default rather than refusing the save, which would
		// strand the report.
		type: grainDisplayName,
		eyeApplied: toEyeApplied(definition.fields),
		privateAccess: DEFAULT_PRIVATE_ACCESS,
		filters: toSavedReportFilters(definition),
		layout: toSavedReportLayout(definition),
		sort: toSavedReportSort(definition),
		// Records that the builder authored this report, so opening it from the Reports list comes back
		// here rather than to the report page a standard report belongs in. Only set on create: a report's
		// origin does not change when it is edited.
		//
		// The audit log is the exception: it renders on the builder's surface but the app built it
		// from a card, with no prompt behind it, so claiming 'AI' would put "Generated from your
		// prompt" above a report nobody prompted for. It still reopens in the builder -- `openReport`
		// and `reportBuilderPathFor` route it on its own type rather than on this field.
		source: grain === AUDIT_LOG_GRAIN ? 'STANDARD' : 'AI',
		promptText,
		// Sent on create as well as update: a report whose conditions were tweaked before its first save
		// has already diverged from its prompt by the time the row is created.
		manuallyEdited,
		datePreset: definition.datePreset ?? null,
		dateScope: definition.dateScope ?? null,
		...toSavedReportGroupings(definition),
		...datesOf(definition),
	};
}

/** Builds the update input for re-saving a report that already exists in the Reports list.
 *
 * Three inputs are load-bearing beyond the definition itself:
 * - `reportService: true` — the mutation resolver routes to report-service only when it is set, and
 *   otherwise writes to the legacy saved-report store, where the report does not exist.
 * - `state` — the update overwrites the stored state unconditionally, so omitting it would clear it
 *   and strand the report outside both the saved and temporary listings.
 * - `privateAccess` — likewise overwritten unconditionally, so the report's current value is echoed
 *   back rather than re-asserting the create-time default over a later sharing change. */
export function buildUpdateSavedReportInput(
	saved: SavedReportRef,
	definition: ReportDefinitionV1,
	manuallyEdited: boolean,
	lockToken?: string | null
): UpdateSavedReportInputFields {
	return {
		id: saved.id,
		// Only when this tab holds a lock. report-service reads a presented token as "this caller
		// participates in the lock protocol" and refuses the write once the token no longer names a live
		// lock it holds, so sending an empty one would turn every unlocked save into a conflict.
		...(lockToken ? {lockToken} : {}),
		name: definition.name,
		eyeApplied: toEyeApplied(definition.fields),
		reportService: true,
		state: ReportState.SAVED,
		privateAccess: saved.privateAccess,
		filters: toSavedReportFilters(definition),
		layout: toSavedReportLayout(definition),
		sort: toSavedReportSort(definition),
		datePreset: definition.datePreset ?? null,
		dateScope: definition.dateScope ?? null,
		// report-service latches this: an update that sends false never lowers a raised flag, so a save
		// from a session that made no manual edit cannot erase one an earlier session made.
		manuallyEdited,
		...toSavedReportGroupings(definition),
		...datesOf(definition),
	};
}
